import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, User, Shield, Crown, Home, LogOut, ArrowRight,
  ChevronDown, MessageCircle, Gamepad2
} from 'lucide-react';
import toast from 'react-hot-toast';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthDropdownOpen, setIsAuthDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAuthDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isMenuOpen) {
        if (!event.target.closest('.menu-button')) {
          setIsMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogout = () => {
    logout();
    toast.success('با موفقیت خارج شدید', {
      icon: '👋',
      style: { borderRadius: '10px', background: '#111827', color: '#fff' }
    });
    navigate('/');
    setIsAuthDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'خانه', icon: Home, exact: true },
    { path: '/ranks', label: 'خرید رنک', icon: Crown },
    { path: '/contact', label: 'ارتباط با ما', icon: MessageCircle },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const menuVariants = {
    hidden: { opacity: 0, x: '100%' },
    visible: { opacity: 1, x: 0, transition: { type: 'spring', damping: 25, stiffness: 200 } },
    exit: { opacity: 0, x: '100%', transition: { duration: 0.3 } }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', damping: 20, stiffness: 300 } },
    exit: { opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.2 } }
  };

  return (
    <>
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 bg-[#050811]/90 backdrop-blur-xl border-b border-white/10 ${
        scrolled ? 'shadow-2xl shadow-emerald-950/20' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo — بدون عکس، با آیکون و گرادینت زمردی/سیان */}
            <Link
              to="/"
              className="flex items-center gap-3 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-cyan-500 blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                <div className="relative w-10 h-10 lg:w-12 lg:h-12 bg-white/10 rounded-xl lg:rounded-2xl shadow-2xl flex items-center justify-center border border-white/20 overflow-hidden group-hover:scale-105 transition-transform duration-300">
                  <img src="/favicon.ico" alt="AvalonMC Logo" className="w-full h-full object-contain p-1 rounded-xl lg:rounded-2xl" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-300 bg-clip-text text-transparent tracking-tighter">
                  AvalonMC
                </span>
                <span className="text-[10px] lg:text-xs text-gray-400 -mt-1">بزرگترین سرور بدراک ایران</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 bg-white/5 backdrop-blur-xl rounded-2xl px-3 py-2 border border-white/10">
              {navLinks.map((link) => {
                const active = isActive(link.path, link.exact);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-5 py-2.5 rounded-xl transition-all duration-300 font-bold group text-sm ${
                      active
                        ? 'text-emerald-400 bg-emerald-500/10'
                        : 'text-gray-300 hover:text-emerald-400 hover:bg-white/5'
                    }`}
                  >
                    <link.icon className={`w-4 h-4 inline-block ml-2 transition-all duration-300 ${
                      active ? 'scale-110 text-emerald-400' : 'group-hover:scale-110'
                    }`} />
                    {link.label}
                    {active && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full"
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Desktop Right Side */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <Link to="/user-panel">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-emerald-500/30 text-emerald-400 font-bold transition-all duration-300 text-sm"
                    >
                      <User className="w-4 h-4 inline-block ml-2" />
                      پنل کاربری
                    </motion.button>
                  </Link>

                  {isAdmin && (
                    <Link to="/admin-panel">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-5 py-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 font-bold transition-all duration-300 text-sm"
                      >
                        <Shield className="w-4 h-4 inline-block ml-2" />
                        ادمین
                      </motion.button>
                    </Link>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLogout}
                    className="px-5 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 font-bold hover:bg-red-500 hover:text-white transition-all duration-300 text-sm"
                  >
                    <LogOut className="w-4 h-4 inline-block ml-2" />
                    خروج
                  </motion.button>
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsAuthDropdownOpen(!isAuthDropdownOpen)}
                    className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-all duration-300 flex items-center gap-2 text-sm shadow-md"
                  >
                    <User className="w-4 h-4" />
                    ورود
                    <motion.div animate={{ rotate: isAuthDropdownOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {isAuthDropdownOpen && (
                      <motion.div
                        variants={dropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-[#090d1a] border border-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl overflow-hidden"
                      >
                        <div className="p-2">
                          <Link
                            to="/login"
                            onClick={() => setIsAuthDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-200 hover:bg-white/10 transition-all duration-300 font-bold text-sm"
                          >
                            <ArrowRight className="w-4 h-4 text-emerald-400" />
                            ورود به حساب کاربری
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={toggleMenu}
              className="lg:hidden p-2.5 rounded-xl bg-white/5 border border-white/10 text-white menu-button"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
            />

            <motion.div
              ref={mobileMenuRef}
              variants={menuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed top-0 right-0 h-full w-full max-w-sm bg-[#050811] border-l border-white/10 shadow-2xl z-50 lg:hidden text-white"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-xl border border-white/20 p-1 flex items-center justify-center overflow-hidden">
                      <img src="/favicon.ico" alt="AvalonMC Logo" className="w-full h-full object-contain" />
                    </div>
                    <span className="text-xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                      AvalonMC
                    </span>
                  </Link>
                  <button onClick={toggleMenu} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {navLinks.map((link) => {
                    const active = isActive(link.path, link.exact);
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 font-bold ${
                          active
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                            : 'text-gray-300 hover:bg-white/5'
                        }`}
                      >
                        <link.icon className={`w-5 h-5 ${active ? 'text-emerald-400' : ''}`} />
                        {link.label}
                      </Link>
                    );
                  })}

                  {/* Auth Section in Mobile */}
                  <div className="pt-6 border-t border-white/10 mt-4">
                    {isAuthenticated ? (
                      <div className="space-y-3">
                        <div className="px-5 py-3 rounded-2xl bg-white/5 border border-white/10">
                          <p className="text-xs text-gray-400">خوش آمدید،</p>
                          <p className="font-bold text-emerald-400">{user?.username}</p>
                        </div>
                        <Link
                          to="/user-panel"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-white/5 border border-emerald-500/30 font-bold text-emerald-400"
                        >
                          <User className="w-5 h-5" />
                          پنل کاربری
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin-panel"
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-red-500/10 border border-red-500/30 font-bold text-red-400"
                          >
                            <Shield className="w-5 h-5" />
                            پنل ادمین
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-red-500/20 text-red-400 border border-red-500/30 font-bold"
                        >
                          <LogOut className="w-5 h-5" />
                          خروج از حساب
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold shadow-lg"
                      >
                        <ArrowRight className="w-5 h-5" />
                        ورود به حساب
                      </Link>
                    )}
                  </div>
                </div>

                <div className="p-6 border-t border-white/10">
                  <p className="text-center text-xs text-gray-500">
                    © 2026 AvalonMC - بزرگترین سرور بدراک ایران
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;